import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axiosInstance from "../axios/axios-instance";
import { pathAPI } from "../utils/pathApi";
import { PermissionCode } from "./permissions";

export type RoleScopeType = "GLOBAL" | "COUNTRY" | "OUTLET";

export interface MeRole {
  id: number;
  code: string;
  name: string;
  scope_type: RoleScopeType;
  multi_country: boolean;
}

export interface MeCountry {
  id: number;
  name: string;
  code: string;
}

export interface Me {
  id: number;
  email: string;
  full_name: string;
  avatar: string | null;
  role: MeRole | null;
  permissions: string[];
  countries: MeCountry[];
}

interface MeContextValue {
  me: Me | null;
  loading: boolean;
  error: boolean;
  isSuperAdmin: boolean;
  can: (permission: PermissionCode) => boolean;
  canAny: (...permissions: PermissionCode[]) => boolean;
  reload: () => void;
}

const SUPER_ADMIN_CODE = "SUPER_ADMIN";

const MeContext = createContext<MeContextValue>({
  me: null,
  loading: true,
  error: false,
  isSuperAdmin: false,
  can: () => false,
  canAny: () => false,
  reload: () => undefined,
});

export const MeProvider = ({ children }: { children: ReactNode }) => {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    axiosInstance
      .get(pathAPI.me)
      .then((res) => {
        const data = res.data?.data as Me;
        setMe(data);
        if (data?.role?.name) {
          localStorage.setItem("current_user_role", data.role.name);
        }
        if (data?.id) {
          localStorage.setItem("current_user_id", String(data.id));
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo<MeContextValue>(() => {
    const granted = new Set(me?.permissions ?? []);
    const isSuperAdmin = me?.role?.code === SUPER_ADMIN_CODE;
    // If /admin/me could not be loaded, do not lock the UI: the server enforces permissions.
    const can = (permission: PermissionCode) => isSuperAdmin || error || granted.has(permission);
    return {
      me,
      loading,
      error,
      isSuperAdmin,
      can,
      canAny: (...permissions) => permissions.some(can),
      reload: load,
    };
  }, [me, loading, error, load]);

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
};

export const useMe = () => useContext(MeContext);
